<template>
    <TimelineLoader v-if="isLoading" :nbItems="5" />

    <div v-else class="timeline">
        <div v-for="(item, index) in items" :key="index" class="timeline-item">
            <div class="timeline-item__icon">
                <img
                    v-if="item.icon === 'gocardless'"
                    src="/ressources/logged/img/gocardless/gocardless.svg"
                    alt="GoCardless icon"
                >
                <CompassColoredIcon
                    v-else
                    :icon="item.icon"
                    :color="getIconColor(item.icon, index === 0)"
                    :size="CompassEnums.ColoredIconSize.LARGE"
                />
            </div>
            <div class="timeline-item__content">
                <div class="timeline-item__main">
                    <p class="typographies-paragraph-medium-semibold timeline-item__title">
                        {{ item.title }}
                    </p>
                    <CompassPill
                        v-if="item.highlightedDate"
                        :variant="CompassEnums.PillVariant.STROKED"
                        :label="formatDateLabel(item.highlightedDate)"
                        :size="CompassEnums.PillSize.EXTRA_SMALL"
                        icon="clock"
                    />
                </div>
                <p class="colors-common-text-standard-caption timeline-item__label">
                    {{ item.label }}
                </p>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { TimelineItem } from 'vcomponents/common/timeline/Timeline.type';
import TimelineLoader from 'vcomponents/common/timeline/TimelineLoader.vue';
import { formatDateLabel } from 'Helpers/dateHelper';

interface Props {
    isLoading: boolean
    items: TimelineItem[];
}

defineProps<Props>();

const mappingColorIcons: Record<string, CompassEnums.ColoredIconColorType> = {
    'success-circle': CompassEnums.ColoredIconColorType.GREEN,
    'alert-circle': CompassEnums.ColoredIconColorType.RED,
    'warning': CompassEnums.ColoredIconColorType.ORANGE,
    'reply': CompassEnums.ColoredIconColorType.ORANGE,
    'plus': CompassEnums.ColoredIconColorType.TEAL,
    'reload': CompassEnums.ColoredIconColorType.TEAL,
    'info-circle': CompassEnums.ColoredIconColorType.TEAL,
    'default': CompassEnums.ColoredIconColorType.GRAY,
};

function getIconColor(icon: string, isFirstItem: boolean): string
{
    if (isFirstItem) {
       return mappingColorIcons[icon] || mappingColorIcons['default'];
    }

    return mappingColorIcons['default'];
}
</script>

<style lang="scss" scoped>
.timeline {
    margin-top: var(--spacing-5);
    margin-inline: var(--spacing-3);
}

.timeline-item {
    display: flex;
    align-items: stretch;
    gap: var(--spacing-3);

    &__icon {
        display: flex;
        flex-direction: column;
        align-items: center;
        flex-shrink: 0;
    }

    &:not(:last-child) &__content {
        padding-bottom: var(--spacing-5);
    }

    &:not(:last-child) &__icon::after {
        content: '';
        flex: 1;
        width: 2px;
        background-color: var(--colors-common-background-subtle);
        margin-block: var(--spacing-1);
    }

    &__main {
        display: flex;
        align-items: center;
    }

    &__title {
        margin: 0 var(--spacing-2) 0 0;
    }

    &__label {
        color: var(--colors-common-background-darker-default);
        margin: var(--spacing-0) 0;
    }
}
</style>
